import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Sidebar from "../../../component/includes/sidebar";
import { HeroContent, heroContentService, UpdateHeroContentData } from "../../../services/hero";
import { Pencil, Search, PlusCircle, X, AlertCircle } from "lucide-react";

const HeroContentManagement: React.FC = () => {
  const [heroContent, setHeroContent] = useState<HeroContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingHeroContent, setEditingHeroContent] = useState<HeroContent | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);

  // Fetch Data
  const fetchHeroContent = async () => {
    try {
      setLoading(true);
      const response = await heroContentService.getHeroContent();
      setHeroContent(response || []);
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to fetch hero content");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHeroContent();
  }, []);

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const showError = (message: string) => {
    setError(message);
    setTimeout(() => setError(null), 3000);
  };

  const resetForm = () => {
    setEditingHeroContent(null);
    setIsModalOpen(false);
    setImagePreview(null);
    setSelectedImageFile(null);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleEditClick = (content: HeroContent) => {
    setEditingHeroContent(content);
    setImagePreview(content.image ? `${process.env.REACT_APP_API_URL}${content.image}` : null);
    setSelectedImageFile(null);
    setIsModalOpen(true);
  };

  const handleUpdateHeroContent = async () => {
    if (!editingHeroContent) return;

    if (!editingHeroContent.title?.trim() || !editingHeroContent.description?.trim()) {
      showError("Title and description cannot be empty");
      return;
    }

    try {
      const updatedData: UpdateHeroContentData = {
        id: editingHeroContent.id,
        title: editingHeroContent.title,
        description: editingHeroContent.description,
        image: selectedImageFile || undefined, // only send new file if changed
      };

      await heroContentService.updateHeroContent(updatedData);
      await fetchHeroContent();
      showSuccess("Hero content updated successfully");
      resetForm();
    } catch (err) {
      showError("Failed to update hero content");
      console.error(err);
    }
  };

  const filteredHeroContent = heroContent.filter((content) =>
    content.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    content.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
<div className="flex">
        <Sidebar />

        <main className="flex-1 p-6 md:p-8 mt-14">
          <div className="max-w-7xl mx-auto">
            {/* Header - Mirip Gambar */}
            <div className="bg-white rounded-3xl shadow-sm p-6 mb-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-2xl flex items-center justify-center">
                <span className="text-white text-3xl">📄</span>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Hero Content Management</h1>
                <p className="text-gray-500">Manage your hero content</p>
              </div>
            </div>

            {/* Search Bar - Mirip Gambar */}
            <div className="relative mb-6">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search hero content by title or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-5 py-4 bg-white border border-gray-200 rounded-3xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
              />
            </div>

            {/* Table */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100 border-b">
                    <tr>
                      <th className="px-6 py-5 text-left text-gray-600 font-semibold w-12">ID</th>
                      <th className="px-6 py-5 text-left text-gray-600 font-semibold w-28">IMAGE</th>
                      <th className="px-6 py-5 text-left text-gray-600 font-semibold">TITLE</th>
                      <th className="px-6 py-5 text-left text-gray-600 font-semibold">DESCRIPTION</th>
                      <th className="px-6 py-5 text-left text-gray-600 font-semibold w-44">CREATED AT</th>
                      <th className="px-6 py-5 text-left text-gray-600 font-semibold w-44">UPDATED AT</th>
                      <th className="px-6 py-5 text-left text-gray-600 font-semibold w-24">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredHeroContent.map((content, index) => (
                      <tr key={content.id} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-6">
                          <div className="w-7 h-7 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-sm font-semibold">
                            {index + 1}
                          </div>
                        </td>

                        <td className="px-6 py-6">
                          <div className="w-20 h-20 bg-gray-100 rounded-2xl overflow-hidden border">
                            {content.image ? (
                              <img
                                src={`${process.env.REACT_APP_API_URL}${content.image}`}
                                alt={content.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">
                                No Image
                              </div>
                            )}
                          </div>
                        </td>

                        <td className="px-6 py-6 font-medium text-gray-800">
                          {content.title}
                        </td>

                        <td className="px-6 py-6 text-gray-600 max-w-md">
                          <div 
                            className="line-clamp-2"
                            dangerouslySetInnerHTML={{ __html: content.description }}
                          />
                        </td>

                        <td className="px-6 py-6 text-gray-600">
                          {new Date(content.created_at).toLocaleDateString("id-ID", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </td>

                        <td className="px-6 py-6 text-gray-600">
                          {new Date(content.updated_at).toLocaleDateString("id-ID", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </td>

                        <td className="px-6 py-6">
                          <button
                            onClick={() => handleEditClick(content)}
                            className="text-blue-500 hover:text-blue-600 transition"
                          >
                            <Pencil size={20} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Total Hero Content */}
              <div className="bg-gray-50 border-t px-8 py-5 text-center text-gray-600 font-medium">
                Total Hero Content: {filteredHeroContent.length}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Edit Modal - Improved Design */}
      {isModalOpen && editingHeroContent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[95vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900">Edit Hero Content</h2>
                <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                  <X size={28} />
                </button>
              </div>

              <div className="space-y-8">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                  <input
                    type="text"
                    value={editingHeroContent.title || ""}
                    onChange={(e) =>
                      setEditingHeroContent({ ...editingHeroContent, title: e.target.value })
                    }
                    className="w-full px-5 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <ReactQuill
                    value={editingHeroContent.description || ""}
                    onChange={(value) =>
                      setEditingHeroContent({ ...editingHeroContent, description: value })
                    }
                    className="bg-white rounded-2xl"
                    theme="snow"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Background Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-2xl file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />

                  {(imagePreview || editingHeroContent.image) && (
                    <div className="mt-6">
                      <p className="text-sm text-gray-500 mb-2">Preview:</p>
                      <img
                        src={imagePreview || `${process.env.REACT_APP_API_URL}${editingHeroContent.image}`}
                        alt="Preview"
                        className="max-h-64 w-full object-cover rounded-2xl border border-gray-200"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-4 mt-10">
                <button
                  onClick={resetForm}
                  className="px-8 py-3 text-gray-600 hover:bg-gray-100 rounded-2xl transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateHeroContent}
                  className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl transition flex items-center gap-2"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notifications */}
      {(successMessage || error) && (
        <div className="fixed top-6 right-6 z-[60] space-y-2">
          {successMessage && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-6 py-4 rounded-2xl shadow-lg flex items-center gap-3">
              <AlertCircle size={24} className="text-green-600" />
              {successMessage}
            </div>
          )}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-2xl shadow-lg flex items-center gap-3">
              <AlertCircle size={24} className="text-red-600" />
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HeroContentManagement;